import { Col0a, Col0b, FewHouseholdsWarning, FreePhonesMessage, LocalStatistics, MapModalTeleph, PhonesToShowPagination, StateOfTerritoryBtn, StaticMap, TelephonicCard } from './subcomponents';
import { H2, Loading, WarningToaster } from 'src/components/_commons';
import { setAlertResponse } from 'src/store';
import { sse } from '@/app-config';
import { sseEvents } from '@monorepo/helpers';
import { telephonicServices, telephonicHelper } from 'src/services';
import { typeBlock, typeHousehold, typeTelephonicTerritory, typeTerritoryNumber } from '@monorepo/models';
import { useDispatch } from 'react-redux';
import { useGetCurrentUser } from 'src/custom-hooks';
import { useNavigate, useParams } from 'react-router';
import { useState, useEffect, useCallback } from 'react';

export const TelephonicPage = () => {
    const [addressToShowInGoogleMaps, setAddressToShowInGoogleMaps] = useState('');
    const [blocks, setBlocks] = useState<typeBlock[]>();
    const [brought, setBrought] = useState(10);
    const [currentBlock, setCurrentBlock] = useState<typeBlock>();
    const [householdsToShow, setHouseholdsToShow] = useState<typeHousehold[]>();
    const [isShowingAllAvailable, setIsShowingAllAvailable] = useState(false);
    const [isShowingAllStates, setIsShowingAllStates] = useState(false);
    const [isShowingStatistics, setIsShowingStatistics] = useState(false);
    const [loaded, setLoaded] = useState(false);
    const [showPagination, setShowPagination] = useState(true);
    const [showWarningToaster, setShowWarningToaster] = useState(false);
    const [telephonicTerritory, setTelephonicTerritory] = useState<typeTelephonicTerritory>();
    const [userEmailWarningToaster, setUserEmailWarningToaster] = useState<string>();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const territoryNumber: typeTerritoryNumber = useParams<any>().territoryNumber as typeTerritoryNumber;
    const user = useGetCurrentUser();

    const setBroughtAllHandler = (): void => {
        setIsShowingAllAvailable(true);
        setShowPagination(false);
    }

    const setCurrentBlockHandler = (value: typeBlock): void => {
        setIsShowingStatistics(false);
        setIsShowingAllAvailable(false);
        setIsShowingAllStates(false);
        setBrought(10);
        setShowPagination(true);
        setCurrentBlock(value);
    }
    
    const setIsShowingAllStatesHandler = (value: boolean): void => {
        setIsShowingStatistics(false);
        setIsShowingAllAvailable(false);
        setIsShowingAllStates(value);
        setBrought(10);
        setShowPagination(true);
    }

    const setIsShowingStatisticsHandler = () => {
        setIsShowingStatistics(true);
        setIsShowingAllAvailable(false);
        setIsShowingAllStates(false);
        setCurrentBlock(undefined);
    }

    const closeWarningToasterHandler = (): void => setShowWarningToaster(false);

    const hideGoogleMapHandler = (): void => setAddressToShowInGoogleMaps('');

    const updateHouseholdsToShow = useCallback(() => {
        if (!currentBlock) {
            return setHouseholdsToShow([]);
        }
        if (!telephonicTerritory) {
            return;
        }
        let _householdsToShow = telephonicHelper.GetHouseholdsToShow(telephonicTerritory.households, currentBlock, isShowingAllStates, isShowingAllAvailable);
        const temp = [..._householdsToShow];
        if (!isShowingAllAvailable) {
            _householdsToShow = _householdsToShow.slice(0, brought);
        }
        if (isShowingAllAvailable || _householdsToShow.length === temp.length) {
            setShowPagination(false);
        }
        _householdsToShow = telephonicHelper.GetHouseholdVariant(_householdsToShow);
        setHouseholdsToShow(_householdsToShow);
    }, [brought, currentBlock, isShowingAllAvailable, isShowingAllStates, telephonicTerritory]);

    useEffect(() => {
        if (!territoryNumber) {
            navigate('/telefonica');
            return;
        }
        telephonicServices.GetTerritory(territoryNumber).then(response => {
            setLoaded(true);
            if (!response.success || !response.data?.telephonicTerritory) {
                dispatch(setAlertResponse({ response }));
                return;
            }
            const blocks = telephonicHelper.GetBlocks(response.data.telephonicTerritory.households);
            setBlocks(blocks);
            setCurrentBlock(blocks[0]);
            setTelephonicTerritory(response.data.telephonicTerritory);
        });
        return () => {
            setBlocks(undefined);
            setUserEmailWarningToaster(undefined);
            setTelephonicTerritory(undefined);
        }
    }, [territoryNumber]);

    useEffect(() => {
        updateHouseholdsToShow();
    }, [brought, currentBlock, isShowingAllAvailable, isShowingAllStates, telephonicTerritory, updateHouseholdsToShow]);
    
    useEffect(() => {
        const handler = (e: MessageEvent) => {
            const data = JSON.parse((e as MessageEvent).data);
            if (data.congregation !== user.congregation) {
                return;
            }
            if (data.territoryNumber !== territoryNumber) {
                return;
            }
            if (data.userEmail !== user.email) {
                setShowWarningToaster(true);
                setUserEmailWarningToaster(data.userEmail);
                if (!data.updatedHousehold) {  // territory reseted or changed state by someone else
                    navigate('/telefonica');
                    return;
                }
            }
            if (!telephonicTerritory || !data.updatedHousehold) {
                return;
            }
            setTelephonicTerritory(x => !x ? x : ({
                ...x,
                households: telephonicTerritory?.households?.map(x =>
                    x.id === data.updatedHousehold.id ?
                        ({ ...data.updatedHousehold, doNotMove: true })
                        :
                        x
                )
            }));
            updateHouseholdsToShow();
        }
        sse.addEventListener(sseEvents.telephonicChange, handler);
        return () => {
            sse.removeEventListener(sseEvents.telephonicChange, handler);
        };
    }, [brought, currentBlock, isShowingAllAvailable, isShowingAllStates, telephonicTerritory, territoryNumber, updateHouseholdsToShow, user.congregation, user.email]);

    return (
        <>
        {addressToShowInGoogleMaps &&
            <MapModalTeleph
                address={addressToShowInGoogleMaps}
                hideGoogleMapHandler={hideGoogleMapHandler}
            />
        }
        {loaded &&
            <div style={{ marginTop: '30px', position: 'fixed', zIndex: 4 }}>
                <FewHouseholdsWarning
                    households={telephonicTerritory?.households}
                />
                {showWarningToaster && userEmailWarningToaster &&
                    <WarningToaster
                        bodyText={["Este territorio está siendo trabajado por el usuario ", <strong key={0}>{userEmailWarningToaster}</strong>]}
                        closeWarningToaster={closeWarningToasterHandler}
                        headerText={<strong>Posible confusión de asignación</strong>}
                    />
                }
            </div>
        }
        <H2 title={'TELEFÓNICA'} mb={'0px'} />
        <H2 title={`TERRITORIO ${territoryNumber} ${telephonicTerritory?.stateOfTerritory.isFinished ? "- TERMINADO" : ''}`}
            mt={'10px'}
            mb={'50px'}
        />
        {!loaded &&
            <Loading mt={'60px'} mb={'10px'} />
        }
        {loaded && !isShowingStatistics && telephonicTerritory?.mapId &&
            <StaticMap
                mapId={telephonicTerritory.mapId}
            />
        }
        {loaded &&
            <>
            <Col0a
                blocks={blocks}
                currentBlock={currentBlock}
                setCurrentBlockHandler={setCurrentBlockHandler}
            />
            <Col0b
                isShowingAll={isShowingAllStates}
                isShowingStatistics={isShowingStatistics}
                setIsShowingAllStatesHandler={setIsShowingAllStatesHandler}
                setIsShowingStatisticsHandler={setIsShowingStatisticsHandler}
            />
            </>
        }
        {isShowingStatistics ?
            <>
            {!!telephonicTerritory ?
                <LocalStatistics
                    telephonicTerritory={telephonicTerritory}
                />
                :
                <>Error en datos de Estadísticas Locales</>
            }
            </>
            :
            <>
            {telephonicTerritory &&
                <>
                <StateOfTerritoryBtn
                    isFinished={telephonicTerritory.stateOfTerritory.isFinished}
                    territoryNumber={territoryNumber}
                />
                <FreePhonesMessage
                    currentBlock={currentBlock}
                    households={telephonicTerritory?.households}
                    loaded={loaded}
                    territoryNumber={territoryNumber}
                />
                </>
            }
            {!!householdsToShow?.length && householdsToShow?.map(household =>
                <TelephonicCard
                    household={household}
                    key={household.id}
                    setAddressToShowInGoogleMaps={setAddressToShowInGoogleMaps}
                    territoryNumber={territoryNumber}
                />
            )}
            {!!householdsToShow?.length && showPagination &&
                <PhonesToShowPagination
                    isShowingAllStates={isShowingAllStates}
                    setBrought={setBrought}
                    setBroughtAllHandler={setBroughtAllHandler}
                />
            }
            </>
        }
        </>
    );
}
